import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getAuthenticatedApiClient, QUERY_KEYS } from "../client";
import type {
  CreateJournalEntryRequest,
  UpdateJournalEntryRequest,
  CreateJournalTagRequest,
} from "../generated/api-client";

interface JournalEntriesParams {
  pageNumber?: number;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: string;
}

interface SearchJournalParams {
  searchText?: string;
  dateFrom?: Date;
  dateTo?: Date;
  productCodePrefix?: string;
  tagIds?: number[];
  createdByUserId?: string;
  pageNumber?: number;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: string;
  /** Only company-wide entries (no product association). */
  withoutProducts?: boolean;
}

export const useJournalEntries = (params: JournalEntriesParams = {}) => {
  return useQuery({
    queryKey: [...QUERY_KEYS.journal, "entries", params],
    queryFn: async () => {
      const client = await getAuthenticatedApiClient();
      return await client.journal_GetJournalEntries(
        params.pageNumber,
        params.pageSize,
        params.sortBy,
        params.sortDirection,
      );
    },
  });
};

/**
 * Searches journal entries. The query is gated by `enabled` so that the page
 * controlling search mode can opt in only when the user has applied a non-empty
 * filter. Default is `false` to preserve the "no request on mount" behavior for
 * any future caller that does not explicitly enable the query.
 *
 * When `enabled` is `true`, React Query auto-refetches on every change to
 * `params` (pagination, page size, sort), so callers should rely on key-change
 * refetch rather than calling `.refetch()` manually after changing state.
 */
export const useSearchJournalEntries = (
  params: SearchJournalParams,
  enabled: boolean = false,
) => {
  return useQuery({
    queryKey: [...QUERY_KEYS.journal, "search", params],
    queryFn: async () => {
      const client = await getAuthenticatedApiClient();
      return await client.journal_SearchJournalEntries(
        params.searchText || undefined,
        params.dateFrom || undefined,
        params.dateTo || undefined,
        params.productCodePrefix || undefined,
        params.tagIds || undefined,
        params.createdByUserId || undefined,
        params.pageNumber,
        params.pageSize,
        params.sortBy,
        params.sortDirection,
        params.withoutProducts || undefined,
      );
    },
    enabled,
  });
};

export const useJournalEntry = (id: number) => {
  return useQuery({
    queryKey: [...QUERY_KEYS.journal, "entry", id],
    queryFn: async () => {
      const client = await getAuthenticatedApiClient();
      return await client.journal_GetJournalEntry(id);
    },
    enabled: id > 0,
  });
};

export const useCreateJournalEntry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateJournalEntryRequest) => {
      const client = await getAuthenticatedApiClient();
      return await client.journal_CreateJournalEntry(request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...QUERY_KEYS.journal, "entries"],
      });
      queryClient.invalidateQueries({
        queryKey: [...QUERY_KEYS.journal, "search"],
      });
      queryClient.invalidateQueries({
        queryKey: [...QUERY_KEYS.journal, "byProduct"],
      });
    },
  });
};

export const useUpdateJournalEntry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      request,
    }: {
      id: number;
      request: UpdateJournalEntryRequest;
    }) => {
      const client = await getAuthenticatedApiClient();
      return await client.journal_UpdateJournalEntry(id, request);
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: [...QUERY_KEYS.journal, "entries"],
      });
      queryClient.invalidateQueries({
        queryKey: [...QUERY_KEYS.journal, "search"],
      });
      queryClient.invalidateQueries({
        queryKey: [...QUERY_KEYS.journal, "byProduct"],
      });
      queryClient.invalidateQueries({
        queryKey: [...QUERY_KEYS.journal, "entry", id],
      });
    },
  });
};

export const useDeleteJournalEntry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const client = await getAuthenticatedApiClient();
      await client.journal_DeleteJournalEntry(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...QUERY_KEYS.journal, "entries"],
      });
      queryClient.invalidateQueries({
        queryKey: [...QUERY_KEYS.journal, "search"],
      });
      queryClient.invalidateQueries({
        queryKey: [...QUERY_KEYS.journal, "byProduct"],
      });
    },
  });
};

export const useJournalTags = () => {
  return useQuery({
    queryKey: [...QUERY_KEYS.journal, "tags"],
    queryFn: async () => {
      const client = await getAuthenticatedApiClient();
      return await client.journal_GetJournalTags();
    },
  });
};

export const useCreateJournalTag = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateJournalTagRequest) => {
      const client = await getAuthenticatedApiClient();
      return await client.journal_CreateJournalTag(request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...QUERY_KEYS.journal, "tags"],
      });
    },
  });
};

export const useJournalEntriesByProduct = (productCode: string) => {
  return useQuery({
    queryKey: [...QUERY_KEYS.journal, "byProduct", productCode],
    queryFn: async () => {
      const client = await getAuthenticatedApiClient();

      // Search for entries where the productCode starts with any stored prefix
      // This allows finding entries associated with product families/prefixes

      return await client.journal_SearchJournalEntries(
        undefined, // searchText
        undefined, // dateFrom
        undefined, // dateTo
        productCode, // productCodePrefix - backend will check if this starts with stored prefixes
        undefined, // tagIds
        undefined, // createdByUserId
        1, // pageNumber
        100, // pageSize - get more entries for detail view
        "entryDate", // sortBy
        "desc", // sortDirection - newest first
        undefined, // withoutProducts
      );
    },
    enabled: !!productCode,
  });
};
